/* eslint-disable react/prop-types */
/* eslint-disable react-native/no-color-literals */
/* eslint-disable react-native/no-inline-styles */

import React from 'react';
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';
import moment from 'moment-timezone';

import { pdfSafeValue, currency } from '@global/helpers';
import Footer from './Footer.web';
import Header from './Header.web';
import { PAGE_SIZE } from './constant';

const NAVY = '#142151';

const buildCustomerLabel = (data) => {
  const code = data.customer?.code || '';
  const name = data.customer?.name || '';
  return code && name ? `${code} - ${name}` : pdfSafeValue(name || code);
};

const buildOutletLabel = (data) => {
  const code = data.outlet?.code || '';
  const name = data.outlet?.name || '';
  return code && name ? `${code} - ${name}` : pdfSafeValue(name || code);
};

const buildVariant = (attributes) => {
  if (!attributes) {
    return '';
  }
  const parts = [];
  for (const key of ['size', 'shade', 'variant', 'non_specify']) {
    if (attributes[key]?.name) {
      parts.push(attributes[key].name);
    }
  }
  return parts.join(', ');
};

const PdfRequestProduct = ({ data }) => {
  const detail = data.detail || {};
  const products = data.products || [];
  const isVn = (detail.is_active_for || []).includes('b2b_vn');

  return (
    <Document>
      <Page size={PAGE_SIZE} style={styles.page} wrap>
        <Header isVn={isVn} />

        <View style={styles.container}>
          {/* Customer + Sales Team row */}
          <View style={styles.customerRow}>
            <View>
              <Text style={styles.customerLabel}>Customer:</Text>
              <Text style={styles.customerValue}>
                {buildCustomerLabel(detail)}
              </Text>
              {detail.outlet?.id ? (
                <Text style={styles.customerValue}>
                  {buildOutletLabel(detail)}
                </Text>
              ) : null}
            </View>
            <Text style={{ textAlign: 'right' }}>
              <Text style={styles.salesTeamLabel}>Sales Team : </Text>
              <Text>{pdfSafeValue(detail.customer?.sales_team?.name)}</Text>
            </Text>
          </View>

          {/* Shipping address */}
          <View style={styles.addressSection}>
            <Text style={styles.addressLabel}>Shipping Address:</Text>
            {detail.address?.name ? (
              <Text style={styles.addressText}>{detail.address.name}</Text>
            ) : null}
            {detail.address?.street ? (
              <Text style={styles.addressText}>{detail.address.street}</Text>
            ) : null}
            {detail.address?.city?.name || detail.address?.state?.name ? (
              <Text style={styles.addressText}>
                {[detail.address?.city?.name, detail.address?.state?.name]
                  .filter(Boolean)
                  .join(', ')}
              </Text>
            ) : null}
            {detail.address?.country?.name ? (
              <Text style={styles.addressText}>
                {detail.address.country.name}
              </Text>
            ) : null}
            {!detail.address ? <Text style={styles.addressText}>-</Text> : null}
          </View>

          {/* Document title */}
          <Text style={styles.title}>
            Request Product #{pdfSafeValue(detail.reference_code)}
          </Text>

          {/* 3-column info row */}
          <View style={styles.infoRow}>
            <View style={[styles.infoCol, { alignItems: 'center' }]}>
              <Text style={styles.infoLabel}>Requestor:</Text>
              <Text style={styles.infoValue}>
                {pdfSafeValue(detail.user?.name)}
              </Text>
            </View>
            <View style={[styles.infoCol, { alignItems: 'center' }]}>
              <Text style={styles.infoLabel}>Department:</Text>
              <Text style={styles.infoValue}>B2B (SDI)</Text>
            </View>
            <View style={[styles.infoCol, { alignItems: 'center' }]}>
              <Text style={styles.infoLabel}>Request Date:</Text>
              <Text style={styles.infoValue}>
                {detail.created_at
                  ? moment(detail.created_at).format('DD/MMM/YYYY')
                  : '-'}
              </Text>
            </View>
          </View>

          {/* Product table */}
          <View style={styles.table}>
            <View style={[styles.tableRow, styles.tableHeader]} fixed>
              <Text style={[styles.cell, { flex: 1 }]}>Description</Text>
              <Text style={[styles.cell, { width: '10%' }]}>Taxes</Text>
              <Text style={[styles.cell, { width: '14%', textAlign: 'right' }]}>
                Qty
              </Text>
              <Text style={[styles.cell, { width: '12%', textAlign: 'right' }]}>
                Unit Price
              </Text>
              <Text style={[styles.cell, { width: '12%', textAlign: 'right' }]}>
                Amount
              </Text>
            </View>

            {products.map((p, i) => {
              const sku = p.combination?.reference || '';
              const variant = buildVariant(p.combination?.attributes);
              const description =
                `${sku ? `[${sku}] ` : ''}${pdfSafeValue(p.name)}` +
                (variant ? ` (Variant: ${variant})` : '');
              return (
                <View key={i} style={styles.tableRow}>
                  <Text style={[styles.cell, { flex: 1 }]}>{description}</Text>
                  <Text style={[styles.cell, { width: '10%' }]}>No Taxes</Text>
                  <Text
                    style={[styles.cell, { width: '14%', textAlign: 'right' }]}>
                    {p.quantity ?? 0} Units
                  </Text>
                  <Text
                    style={[styles.cell, { width: '12%', textAlign: 'right' }]}>
                    {currency(0, true)}
                  </Text>
                  <Text
                    style={[styles.cell, { width: '12%', textAlign: 'right' }]}>
                    {currency(0, true)}
                  </Text>
                </View>
              );
            })}
          </View>

          {/* Totals (right-aligned) */}
          <View style={styles.totalsWrapper}>
            <View style={styles.totalsInner}>
              <View style={styles.totalRow}>
                <Text style={styles.totalLabel}>Subtotal</Text>
                <Text style={styles.totalValue}>{currency(0, true)}</Text>
              </View>
              <View style={styles.totalRow}>
                <Text style={styles.totalLabel}>Taxes</Text>
                <Text style={styles.totalValue}>{currency(0, true)}</Text>
              </View>
              <View style={[styles.totalRow, styles.totalRowBold]}>
                <Text style={[styles.totalLabel, { fontWeight: 700 }]}>
                  Total
                </Text>
                <Text style={[styles.totalValue, { fontWeight: 700 }]}>
                  {currency(0, true)}
                </Text>
              </View>
            </View>
          </View>

          {/* Notes (optional) */}
          {detail.notes ? (
            <View style={styles.notesSection}>
              <Text style={styles.notesText}>{detail.notes}</Text>
            </View>
          ) : null}

          {/* Signatures */}
          <View style={styles.signatureWrapper} wrap={false}>
            {['Prepared By', 'Shipped By', 'Received By'].map((label) => (
              <View
                key={label}
                style={[styles.signatureBlock, { alignItems: 'center' }]}>
                <Text style={styles.signatureLabel}>{label} :</Text>
              </View>
            ))}
          </View>
        </View>

        <Footer />
      </Page>
    </Document>
  );
};

const styles = StyleSheet.create({
  page: {
    fontSize: 10,
    fontFamily: 'Roboto',
    paddingTop: 15,
    paddingBottom: 30,
    color: '#000',
  },
  container: {
    paddingHorizontal: 20,
  },

  /* Customer + Sales Team */
  customerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 6,
    fontSize: 8,
    lineHeight: 1.5,
  },
  customerLabel: {
    fontWeight: 700,
  },
  customerValue: {},
  salesTeamLabel: {
    fontWeight: 700,
  },

  /* Shipping address */
  addressSection: {
    marginBottom: 10,
    fontSize: 8,
    lineHeight: 1.5,
  },
  addressLabel: {
    fontWeight: 700,
    marginBottom: 2,
  },
  addressText: {},

  /* Title */
  title: {
    fontSize: 14,
    fontWeight: 700,
    marginBottom: 10,
    color: NAVY,
  },

  /* 3-col info */
  infoRow: {
    flexDirection: 'row',
    marginBottom: 14,
    borderBottomWidth: 0.5,
    borderBottomColor: '#ddd',
    paddingBottom: 10,
    fontSize: 8,
  },
  infoCol: {
    flex: 1,
  },
  infoLabel: {
    color: NAVY,
    fontWeight: 700,
    marginBottom: 2,
  },
  infoValue: {
    color: '#000',
  },

  /* Table */
  table: {
    marginBottom: 0,
    fontSize: 8,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 0.5,
    borderBottomColor: '#ccc',
    paddingVertical: 6,
    alignItems: 'center',
  },
  tableHeader: {
    color: NAVY,
    fontWeight: 700,
    borderBottomColor: '#000',
    borderBottomWidth: 1,
  },
  cell: {
    paddingHorizontal: 4,
  },

  /* Totals */
  totalsWrapper: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginBottom: 10,
    fontSize: 8,
  },
  totalsInner: {
    width: '38%',
    borderTopWidth: 0.5,
    borderTopColor: '#ccc',
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    borderBottomWidth: 0.5,
    borderBottomColor: '#ccc',
    paddingVertical: 4,
    paddingHorizontal: 4,
  },
  totalRowBold: {
    borderBottomWidth: 1,
    borderBottomColor: '#000',
  },
  totalLabel: {
    flex: 1,
  },
  totalValue: {
    textAlign: 'right',
  },

  /* Notes */
  notesSection: {
    marginTop: 8,
    marginBottom: 16,
    fontSize: 8,
    lineHeight: 1.6,
  },
  notesText: {},

  /* Signatures */
  signatureWrapper: {
    flexDirection: 'row',
    marginTop: 60,
    fontSize: 8,
  },
  signatureBlock: {
    flex: 1,
  },
  signatureLabel: {},
});

export default PdfRequestProduct;
